import {
  ActivityFilterKey,
  FeatureName,
  hasAssetAccess,
  isGuestWorkspaceRole,
  useAssetChatSubject,
  useAssetChatUnreadCount,
  useCurrentAccount,
  useCurrentWorkspace,
  useIsFeatureEnabledForWorkspace,
  useRoles,
} from '@gripp/shared-logic';
import {
  Colors,
  CommentIcon,
  SlideAction,
  tokens,
  useSlideLayout,
} from '@gripp/shared-ui';
import { startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';
import {
  isActivityOrEditPage,
  shouldUseAssetSubrouteInitialTransition,
} from '../assetUtils';

/**
 * The asset chat entry point, in the asset page's column header.
 *
 * Lives in its own module because the asset page has three composition
 * roots and all of them need it: React Router matches
 * `assetDetailPanel.tsx`'s nested `:assetId` route for the bare
 * `/assets/:assetId` URL, `assetDetail.tsx` owns the flat
 * `:assetId/activity/all` entry and the workspaces prefix, and
 * `activityConversationPanel.tsx` renders the asset reached from an
 * activity. Render it from every header; do not re-implement it in any.
 *
 * Owns its own hooks, all called unconditionally with no early return above
 * them; the gate is applied only to the returned JSX.
 */
export const AssetChatHeaderButton = ({
  assetId,
  routeWorkspaceId,
}: {
  assetId: string;
  routeWorkspaceId?: string;
}) => {
  const { t } = useTranslation();
  // Owns its own access gating rather than taking it as props. Three
  // composition roots render this button — assetDetailPanel.tsx (bare
  // /assets/:assetId), assetDetail.tsx (:assetId/activity/all and the
  // workspaces prefix) and activityConversationPanel.tsx
  // (/activities/:issueId/asset/:assetId) — and passing the gate down meant
  // each recomputed it, so the one that did not got an ungated or missing
  // button. Fails closed: unresolved account/roles/workspace data reads as
  // guest, matching each root's own allowlist ternary.
  const { onSlideChange, prepareSlideTransition } = useSlideLayout();
  const currentWorkspace = useCurrentWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath } = useAssetRouting(assetId);

  // Gated against the ASSET's workspace, not the caller's current one — see
  // useAssetChatSubject's own doc comment for the full rationale. The
  // workspace is known before the subject row is (route prefix, else the
  // active workspace), so the flag is read first and the row is only fetched
  // once the flag is on: with it off, the asset page issues no extra query.
  const chatWorkspaceId = routeWorkspaceId ?? currentWorkspace?.id;
  const { isEnabled: isChatFeatureEnabled, isLoading: isChatFeatureLoading } =
    useIsFeatureEnabledForWorkspace(
      FeatureName.AssetConversation,
      chatWorkspaceId
    );
  const { subject: chatSubject, isLoading: isChatSubjectLoading } =
    useAssetChatSubject({
      assetId,
      routeWorkspaceId,
      enabled: !isChatFeatureLoading && isChatFeatureEnabled,
    });
  const { account, isLoading: isAccountLoading } = useCurrentAccount();
  const { roles, isLoading: isRolesLoading } = useRoles();
  // Gate on the PINNED row from useAssetChatSubject, not useWorkspaceAsset:
  // the latter queries by asset id alone and reads an arbitrary items[0]
  // across a multi-workspace asset, so on a shared asset it can
  // decide guest/access against a different workspace than the feed is
  // scoped to. Fails closed: unresolved account/roles/subject reads as guest.
  const subjectWorkspaceAsset = chatSubject?.workspaceAsset;
  const isGuestOfAssetWorkspace =
    isAccountLoading || isRolesLoading || !chatWorkspaceId
      ? true
      : isGuestWorkspaceRole(chatWorkspaceId, account?.workspaceRoles, roles);
  const hasAccess = hasAssetAccess(account, subjectWorkspaceAsset);
  // hasAccess excludes a lonely larry (member of some other workspace, no
  // access to THIS one) — findIssues would refuse their workspaceOnly query
  // as a non-member regardless, so this avoids offering a dead end.
  const canOpenChat =
    !isGuestOfAssetWorkspace &&
    hasAccess &&
    !isChatSubjectLoading &&
    !isChatFeatureLoading &&
    isChatFeatureEnabled &&
    !!chatSubject;
  // The badge: one per feed row carrying unread activity, never the viewer's
  // own posts (see useAssetChatUnreadCount for why it counts rows rather than
  // messages) — only queried once the icon is actually going to show.
  const { count: chatUnreadCount } = useAssetChatUnreadCount({
    workspaceAssetId: chatSubject?.workspaceAssetId ?? '',
    assetId,
    workspaceId: chatWorkspaceId ?? '',
    filterKey: ActivityFilterKey.AssetChat,
    enabled: canOpenChat,
  });

  const onOpenChat = async () => {
    try {
      await openChat();
    } catch (error) {
      console.error(error);
    }
  };

  const openChat = async () => {
    if (shouldUseAssetSubrouteInitialTransition(location)) {
      prepareSlideTransition(SlideAction.NEXT);
      navigate(`${basePath}/chat`, {
        state: { slideLayoutInitialTransition: SlideAction.NEXT },
      });
      return;
    }

    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    startTransition(() => navigate(`${basePath}/chat`));
  };

  if (!canOpenChat) {
    return null;
  }

  return (
    <Pressable
      style={styles.chatButton}
      onPress={onOpenChat}
      accessibilityRole="button"
      accessibilityLabel={
        chatUnreadCount > 0
          ? t('chat.icon.labelWithUnread', { count: chatUnreadCount })
          : t('chat.icon.label')
      }
      testID="asset-card-chat-button"
    >
      <CommentIcon size={20} color={Colors.role.textStrong} />
      {chatUnreadCount > 0 && (
        <View style={styles.chatBadge} testID="asset-card-chat-badge">
          <Text style={styles.chatBadgeText} numberOfLines={1}>
            {chatUnreadCount}
          </Text>
        </View>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  chatButton: {
    position: 'relative',
  },
  chatBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    minWidth: 14,
    height: 14,
    borderRadius: 2,
    paddingHorizontal: 3,
    backgroundColor: Colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatBadgeText: {
    fontFamily: tokens.typography.label.smallEmphasised.fontFamily,
    fontSize: 9,
    lineHeight: 11,
    fontWeight: tokens.typography.label.smallEmphasised.fontWeight,
    color: Colors.white,
  },
});
